import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StripeElementsForm from './StripeElementsForm';

interface PaymentFormProps {
  amount: number;
  orderData: any;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
}

const PaymentForm = ({ amount, orderData, onSuccess, onError }: PaymentFormProps) => {
  const [error, setError] = useState<string | null>(null);

  const handleDemoPayment = async () => {
    // Keep demo payment for testing
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockPaymentResult = {
        success: true,
        orderId: 'ord_' + Math.random().toString(36).substr(2, 9),
        orderNumber: 'ORD-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + Math.floor(Math.random() * 1000000).toString().padStart(6, '0'),
        paymentIntentId: 'pi_' + Math.random().toString(36).substr(2, 24),
        paymentStatus: 'paid',
        amount: amount,
        customerName: orderData.customerName,
        customerEmail: orderData.customerEmail
      };

      onSuccess(mockPaymentResult);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed. Please try again.';
      setError(errorMessage);
      onError(errorMessage);
    }
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="stripe" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="stripe">Real Payment</TabsTrigger>
          <TabsTrigger value="demo">Demo Payment</TabsTrigger>
        </TabsList>
        
        <TabsContent value="stripe" className="space-y-4">
          <div className="p-4 border rounded-lg bg-blue-50/50 border-blue-200 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Real Stripe Payment:</strong> Use your actual card details. This will create a real payment intent.
            </p>
          </div>
          
          <StripeElementsForm
            amount={amount}
            orderData={orderData}
            onSuccess={onSuccess}
            onError={onError}
          />
        </TabsContent>
        
        <TabsContent value="demo" className="space-y-4">
          <div className="p-4 border rounded-lg bg-green-50/50 border-green-200 mb-4">
            <p className="text-sm text-green-800">
              <strong>Demo Payment:</strong> Simulates a successful payment without charging any card.
            </p>
          </div>
          
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleDemoPayment}
            className="w-full"
            size="lg"
          >
            Process Demo Payment (${amount.toFixed(2)})
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface StripePaymentFormProps {
  amount: number;
  orderData: any;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
}

const StripePaymentForm = ({ amount, orderData, onSuccess, onError }: StripePaymentFormProps) => {
  return (
    <PaymentForm
      amount={amount}
      orderData={orderData}
      onSuccess={onSuccess}
      onError={onError}
    />
  );
};

export default StripePaymentForm;
